import { ACTIVE_CITY, selectCity } from '../world/cities.js';
import { BOOT_QUALITY, IS_ULTRA, QUALITY_CHOICES } from '../scene/qualityLevel.js';
import { PerfMeter } from './PerfMeter.js';

/**
 * Settings — popup de configurações do admin (canto superior direito).
 *   • Posição do sol  → horário do dia + iluminação (stage.setSun)
 *   • Cidade          → alterna entre as 4 cidades (recarrega o jogo)
 */
export class Settings {
  constructor(stage, entities, service, hud, clouds) {
    this.stage = stage;
    this.entities = entities;
    this.service = service;
    this.hud = hud;
    this.clouds = clouds;
    this.panel = document.getElementById('settings-panel');
    const btn = document.getElementById('settings-btn');
    const dock = document.getElementById('settings-dock');
    const close = document.getElementById('settings-close');
    const slider = document.getElementById('sun-slider');
    const timeLabel = document.getElementById('sun-time');

    // enquanto o painel está aberto, mantém o botão VISÍVEL (a dock por padrão só o
    // revela no hover) — senão ele sumiria ao mover o mouse p/ dentro do painel.
    const open = () => {
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      dock?.classList.add('is-pinned');
    };
    const hide = () => {
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      dock?.classList.remove('is-pinned');
    };
    const toggle = () =>
      this.panel.classList.contains('is-open') ? hide() : open();

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      toggle();
    });
    close.addEventListener('click', hide);

    // Fecha ao clicar fora ou com ESC
    document.addEventListener('click', (e) => {
      if (
        this.panel.classList.contains('is-open') &&
        !this.panel.contains(e.target) &&
        !btn.contains(e.target)
      ) {
        hide();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') hide();
    });

    // Sol → horário/iluminação (aplica em tempo real ao arrastar).
    //
    // COALESCIDO POR QUADRO: cada `input` do slider chama setSun, que marca o
    // shadow map (congelado) para re-renderizar. Um arraste dispara dezenas de
    // eventos por quadro, e no nível Ultra o mapa tem 2048² — cada um seria um
    // re-render de 4,2M px com ~340 draw calls. Um rAF por quadro dá exatamente o
    // mesmo resultado visual (só o último valor do quadro importa) sem a rajada.
    let sunPend = 0;
    const applySun = () => {
      const hour = parseFloat(slider.value);
      stage.setSun(hour);
      timeLabel.textContent = formatHour(hour);
      sunPend = 0;
    };
    slider.addEventListener('input', () => {
      if (sunPend) return;
      sunPend = requestAnimationFrame(applySun);
    });
    applySun(); // sincroniza rótulo + iluminação com o valor inicial do slider

    // Orientação do sol (N/L/S/O) → gira de qual direção ele ilumina a cidade
    const orientEl = document.getElementById('sun-orient');
    if (orientEl) {
      const applyOrient = () =>
        stage.setSunAzimuth((parseFloat(orientEl.value) * Math.PI) / 180);
      orientEl.addEventListener('change', applyOrient);
      applyOrient(); // aplica a orientação inicial do seletor (Oeste) ao carregar
    }

    // Câmera dinâmica: liga/desliga o seguimento automático dos jogadores
    const dynCam = document.getElementById('dyn-cam');
    if (dynCam) {
      dynCam.checked = !!stage.dynamicCam;
      dynCam.addEventListener('change', () => stage.setDynamicCamera(dynCam.checked));
    }

    // Nuvens: mostra/oculta a camada de nuvens do cenário. Persiste (localStorage) —
    // ligado por padrão; só desliga se explicitamente salvo como '0'.
    const cloudsEl = document.getElementById('clouds-toggle');
    if (cloudsEl && clouds) {
      const applyClouds = (on) => {
        clouds.visible = on;
        try { localStorage.setItem('wab.clouds', on ? '1' : '0'); } catch { /* sem storage */ }
      };
      let on = true;
      try { on = localStorage.getItem('wab.clouds') !== '0'; } catch { /* padrão ligado */ }
      cloudsEl.checked = on;
      applyClouds(on);
      cloudsEl.addEventListener('change', () => applyClouds(cloudsEl.checked));
    }

    // Screenshot em alta resolução (p/ impressão em lona/banner): calcula as dimensões
    // do preset preservando a PROPORÇÃO da tela (borda longa = alvo) e baixa um PNG.
    const shotSel = document.getElementById('shot-res');
    const shotDims = document.getElementById('shot-dims');
    const shotBtn = document.getElementById('shot-btn');
    if (shotSel && shotDims && shotBtn) {
      const LONG_EDGE = { '4k': 3840, '6k': 5760, '8k': 7680 };
      const computeDims = () => {
        const W = window.innerWidth, H = window.innerHeight;
        if (shotSel.value === '2x') return { w: even(W * 2), h: even(H * 2) };
        const long = LONG_EDGE[shotSel.value] || 3840;
        const ar = W / H;
        const [w, h] = ar >= 1 ? [long, long / ar] : [long * ar, long];
        return { w: even(w), h: even(h) };
      };
      const refreshDims = () => {
        const { w, h } = computeDims();
        shotDims.textContent = `${w}×${h}`;
      };
      shotSel.addEventListener('change', refreshDims);
      window.addEventListener('resize', refreshDims);
      refreshDims();

      shotBtn.addEventListener('click', async () => {
        if (shotBtn.disabled) return;
        const { w, h } = computeDims();
        const original = shotBtn.textContent;
        shotBtn.disabled = true;
        shotBtn.textContent = 'Gerando…';
        try {
          const blob = await stage.captureScreenshot(w, h);
          if (!blob) {
            shotBtn.textContent = 'Falhou (resolução alta demais)';
          } else {
            downloadBlob(blob, `wab-smarttown-${w}x${h}-${stamp()}.png`);
            shotBtn.textContent = 'Pronto! ✓';
          }
        } catch (err) {
          console.error('[screenshot]', err);
          shotBtn.textContent = 'Erro ao gerar';
        } finally {
          setTimeout(() => {
            shotBtn.textContent = original;
            shotBtn.disabled = false;
          }, 1800);
        }
      });
    }

    // Qualidade gráfica: resolução de render + antialiasing (o que domina o custo
    // em GPU integrada). Precedência: ?q=<nível> na URL (útil p/ fixar num kiosk)
    // → localStorage → 'alta' (o visual de referência, padrão).
    //
    // 'auto' = RESOLUÇÃO AUTOMÁTICA (setAutoResolution): a escada 1.0→0.9→0.85→0.75
    // sobe/desce sozinha pelo tempo OCUPADO do quadro, com ?fps=N respeitado como
    // orçamento e cada degrau aplicando o perfil completo (resolução+bloom+blur).
    // Os três defeitos que a mantinham fora do painel foram corrigidos — ver
    // autoRes.js e scripts/autoResHarness.mjs. Escolher um perfil MANUAL desliga
    // a automática (setQuality zera autoRes.on).
    const qualEl = document.getElementById('quality-select');
    const qualVal = document.getElementById('quality-val');
    if (qualEl) {
      const valid = (v) => QUALITY_CHOICES.includes(v);
      // Em Ultra o pixelRatio pode passar de 1 (é ALVO, não teto), e aí a
      // porcentagem sozinha mentiria: mostramos o TAMANHO REAL do buffer, que é o
      // número que o operador compara com a resolução da TV.
      const showPr = () => {
        if (!qualVal) return;
        const buf = stage.renderer?.domElement;
        const pct = `${Math.round(stage.maxPixelRatio * 100)}%`;
        qualVal.textContent = stage.maxPixelRatio > 1 && buf
          ? `${buf.width}×${buf.height}`
          : pct;
      };
      const applyQuality = (level, persist) => {
        if (level === 'auto') {
          stage.setAutoResolution(true); // alvo padrão: sustentar 30 fps sem fpsCap
          qualEl.value = 'auto';
        } else {
          stage.setQuality(level); // também desliga a automática
          qualEl.value = stage.quality;
        }
        showPr();
        if (persist) {
          try { localStorage.setItem('wab.quality', level === 'auto' ? 'auto' : stage.quality); } catch { /* sem storage */ }
        }
      };
      // em Ultra o rótulo é o tamanho do buffer, que MUDA ao redimensionar a
      // janela (o % dos outros perfis não mudava, por isso não havia listener)
      window.addEventListener('resize', showPr);
      // uma só verdade sobre "que nível é este" — ?q= > localStorage > alta
      applyQuality(valid(BOOT_QUALITY) ? BOOT_QUALITY : 'alta', false);
      qualEl.addEventListener('change', () => {
        // ENTRAR ou SAIR do Ultra precisa recarregar. Metade dele é resolução de
        // TEXTURA (chão, fachadas, manchas de sombra, copa dos arbustos) e essas
        // são pintadas antes deste painel existir. Recarregar também evita o pior
        // momento do Ultra: aplicá-lo em cima da cena viva realoca todos os alvos
        // do composer num quadro só, somando os antigos ainda não coletados com os
        // novos — num telão 4K isso derruba o contexto WebGL mesmo quando o estado
        // final caberia. Mesmo caminho que a troca de MSAA e a de cidade já usam.
        const alvo = qualEl.value;
        if ((alvo === 'ultra') !== IS_ULTRA) {
          try { localStorage.setItem('wab.quality', alvo); } catch { /* sem storage */ }
          const u = new URL(location.href);
          u.searchParams.delete('q'); // o ?q= venceria o valor recém-salvo
          location.replace(u.toString());
          return;
        }
        applyQuality(alvo, true);
      });
      // em modo automático o % exibido acompanha o degrau vigente: atualiza a cada
      // interação com o painel (barato; o valor muda no máximo a cada 3 s)
      this.panel?.addEventListener('click', showPr);
    }

    // Antialiasing: 5 combinações para comparar na tela real. O MSAA do canvas é
    // definido na criação do contexto WebGL, então mudar de/para ele recarrega.
    const aaEl = document.getElementById('aa-select');
    const aaNote = document.getElementById('aa-note');
    if (aaEl) {
      aaEl.value = stage.aaMode;
      if (aaNote) aaNote.textContent = stage.aaMode;
      // os PERFIS (e a escada automática) podem trocar o AA quando o usuário nunca
      // escolheu um à mão — sem isto o seletor mostrava o modo antigo até recarregar
      this.panel?.addEventListener('click', () => {
        if (aaEl.value !== stage.aaMode) {
          aaEl.value = stage.aaMode;
          if (aaNote) aaNote.textContent = stage.aaMode;
        }
      });
      aaEl.addEventListener('change', () => {
        const precisaReload = stage.setAAMode(aaEl.value);
        if (aaNote) aaNote.textContent = stage.aaMode;
        if (precisaReload) {
          // tira o ?aa= da URL para o valor persistido valer no recarregamento
          const u = new URL(location.href);
          u.searchParams.delete('aa');
          location.replace(u.toString());
        }
      });
    }

    // Medidor de performance (debug): painel à esquerda com GPU em uso, FPS,
    // CPU/GPU por frame, draw calls etc. Persistido (localStorage) — sobrevive a
    // reloads, útil p/ diagnosticar uma máquina específica. Criado LAZY (custo
    // zero p/ quem nunca liga).
    const perfEl = document.getElementById('perf-toggle');
    if (perfEl) {
      const applyPerf = (on) => {
        if (on) (this._perf || (this._perf = new PerfMeter(stage, entities))).enable();
        else this._perf?.disable();
        try { localStorage.setItem('wab.perf', on ? '1' : '0'); } catch { /* sem storage */ }
      };
      let saved = false;
      try { saved = localStorage.getItem('wab.perf') === '1'; } catch { /* sem storage */ }
      perfEl.checked = saved;
      if (saved) applyPerf(true);
      perfEl.addEventListener('change', () => applyPerf(perfEl.checked));
    }

    // Etiquetas dos jogadores: exibir/ocultar + tamanho da fonte + distância (com linha conectora)
    const showEl = document.getElementById('label-show');
    const sizeEl = document.getElementById('label-size');
    const sizeVal = document.getElementById('label-size-val');
    const distEl = document.getElementById('label-dist');
    const distVal = document.getElementById('label-dist-val');
    const applyLabels = () => {
      const fontScale = parseFloat(sizeEl.value);
      const distance = parseFloat(distEl.value);
      sizeVal.textContent = `${Math.round(fontScale * 100)}%`;
      distVal.textContent = distance.toFixed(1);
      entities?.setLabelConfig({ fontScale, distance });
    };
    sizeEl.addEventListener('input', applyLabels);
    distEl.addEventListener('input', applyLabels);
    applyLabels();

    // Exibir nomes: chave-mestra das etiquetas. Ao ocultar, esmaece os sliders de
    // tamanho/distância (que deixam de ter efeito visível).
    if (showEl) {
      const applyShow = () => {
        const on = showEl.checked;
        entities?.setLabelsVisible(on);
        sizeEl.disabled = distEl.disabled = !on;
        sizeEl.style.opacity = distEl.style.opacity = on ? '' : '0.4';
      };
      showEl.addEventListener('change', applyShow);
      applyShow();
    }

    // População simulada: rótulo segue ao arrastar; a regeneração (pesada)
    // só dispara ao soltar ('change') ou clicar num preset.
    const popEl = document.getElementById('pop-slider');
    const popVal = document.getElementById('pop-val');
    const presets = document.getElementById('pop-presets');
    if (service?.live) {
      // ao vivo: o backend controla a população → oculta o controle simulado
      document.getElementById('pop-section')?.style.setProperty('display', 'none');
    } else if (popEl && service) {
      // sincroniza com a população inicial (pode vir de ?players=N na URL)
      const initial = service.population ?? parseInt(popEl.value, 10);
      if (initial > parseInt(popEl.max, 10)) popEl.max = String(initial);
      popEl.value = String(initial);
      popVal.textContent = String(initial);

      const previewPop = () => {
        popVal.textContent = popEl.value;
      };
      const applyPop = (n) => {
        const count = service.setPopulation(n);
        popEl.value = String(count);
        popVal.textContent = String(count);
        hud?.setOnline(count);
      };
      popEl.addEventListener('input', previewPop);
      popEl.addEventListener('change', () => applyPop(parseInt(popEl.value, 10)));
      presets?.addEventListener('click', (e) => {
        const n = e.target?.dataset?.pop;
        if (n) applyPop(parseInt(n, 10));
      });
    }

    // Colisão dos carros por contexto (cruzamentos/curvas/balizas/retas). Persiste no
    // localStorage e é aplicada no CityAgents (service.agents) — sobrevive a um refresh
    // forçado pelo admin. Padrões: cruzamentos OFF, curvas ON, balizas OFF, retas ON.
    this._wireCollision(service);

    // Cidade: marca a ativa e, ao trocar, persiste + recarrega para reconstruir.
    const cityEl = document.getElementById('city-select');
    if (cityEl) {
      cityEl.value = ACTIVE_CITY.id;
      cityEl.addEventListener('change', () => {
        if (cityEl.value !== ACTIVE_CITY.id) selectCity(cityEl.value);
      });
    }
  }

  /** Liga os 4 toggles de colisão por contexto: carrega do localStorage (ou padrões),
   *  aplica no CityAgents e persiste a cada mudança. */
  _wireCollision(service) {
    const els = {
      intersection: document.getElementById('col-intersection'),
      curve: document.getElementById('col-curve'),
      uturn: document.getElementById('col-uturn'),
      straight: document.getElementById('col-straight')
    };
    if (!els.intersection) return;
    let cfg = { ...COLLISION_DEFAULTS };
    try {
      const saved = localStorage.getItem('wab.collision');
      if (saved) cfg = { ...cfg, ...JSON.parse(saved) };
    } catch { /* sem storage → padrões */ }
    const apply = () => {
      for (const k in els) cfg[k] = !!els[k].checked;
      try { localStorage.setItem('wab.collision', JSON.stringify(cfg)); } catch { /* sem storage */ }
      service?.agents?.setCollisionConfig(cfg);
    };
    for (const k in els) {
      els[k].checked = cfg[k];
      els[k].addEventListener('change', apply);
    }
    service?.agents?.setCollisionConfig(cfg); // aplica o estado carregado já no load (sobrevive ao refresh)
  }
}

const COLLISION_DEFAULTS = { intersection: false, curve: true, uturn: false, straight: true };

/** 15.5 → "15:30" */
function formatHour(h) {
  const hh = Math.floor(h) % 24;
  const mm = Math.round((h - Math.floor(h)) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Arredonda p/ um inteiro PAR ≥ 2 (encoders de imagem preferem dimensões pares). */
function even(n) {
  const v = Math.round(n);
  return Math.max(2, v - (v % 2));
}

/** Carimbo de data/hora "aaaa-mm-dd-hh-mm-ss" p/ o nome do arquivo. */
function stamp() {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
}

/** Baixa um Blob como arquivo (cria um <a> temporário e revoga a URL depois). */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
